import axios, { AxiosRequestConfig } from "axios";

import { getErrorDetails } from "api/helpers/getErrorDetails";
import { paramsSerializer } from "api/helpers/paramsSerializer";
import { portalApi, TAGS } from "api/rtkApi";
import { DEFAULT_PAGINATION } from "constants/defaultPagination";
import { SERVICES } from "constants/environment";

import type { UUID } from "utils";
import type { ObjectWithId } from "utils/ObjectWithId";

import type {
  CashManagementAccount,
  SettlementBalance,
  SettlementLimit,
  SettlementScheduleConfig,
} from "../models/CashManagement";

export type GetCashManagementAccounts = (
  id: UUID,
  active: boolean | undefined,
  options?: AxiosRequestConfig
) => Promise<{ list: CashManagementAccount[] }>;

export type GetCashManagementAccount = (
  id: UUID,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PostCashManagementAccount = (
  id: UUID,
  data: CashManagementAccount,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PatchCashManagementAccount = (
  id: UUID,
  data: Partial<CashManagementAccount>,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export const getCashManagementAccounts: GetCashManagementAccounts = async (
  id,
  active,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/cash-management-accounts",
      params: {
        ...DEFAULT_PAGINATION,
        customer: id,
        active: active !== undefined ? active : undefined,
      },
      paramsSerializer,
    });
    const { data = {} } = response;

    return { list: data };
  } catch (error) {
    throw getErrorDetails(error);
  }
};

/**
 * @deprecated Do not use it in the new code base. Use `useGetCashManagementAccountQuery` from `modules/connect` instead.
 * @see useGetCashManagementAccountQuery
 */
export const getCashManagementAccount: GetCashManagementAccount = async (
  id,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/cash-management-accounts/${id}`,
    });
    const { data = {} } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
};

type GetAccountsRequest = ObjectWithId;
type GetAccountsResponse = CashManagementAccount[];
type GetProviderBalanceRequest = ObjectWithId;
type GetProviderBalanceResponse = { amount: number };

type GetSettlementScheduleConfigRequest = ObjectWithId;
type GetSettlementScheduleConfigResponse = SettlementScheduleConfig;

type GetSettlementBalanceRequest = ObjectWithId;
type GetSettlementBalanceResponse = SettlementBalance;

type GetSettlementLimitRequest = ObjectWithId;
type GetSettlementLimitResponse = SettlementLimit;

export const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getCMAccounts: builder.query<GetAccountsResponse, GetAccountsRequest>({
      query: ({ id }) => ({
        url: `/cash-management-accounts?customer=${id}&limit=1000&page=1&active=true`,
        method: "GET",
      }),
      providesTags: [TAGS.CASH_MANAGEMENT_ACCOUNTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getProviderBalance: builder.query<
      GetProviderBalanceResponse,
      GetProviderBalanceRequest
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/provider-balance`,
        method: "GET",
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getSettlementScheduleConfig: builder.query<
      GetSettlementScheduleConfigResponse,
      GetSettlementScheduleConfigRequest
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/settlement-schedule-config`,
        method: "GET",
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getSettlementBalance: builder.query<
      GetSettlementBalanceResponse,
      GetSettlementBalanceRequest
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/settlement-balance`,
        method: "GET",
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getSettlementLimit: builder.query<
      GetSettlementLimitResponse,
      GetSettlementLimitRequest
    >({
      query: ({ id }) => ({
        url: `/portal/cash-management-accounts/${id}/reserve-adjustment`,
        method: "GET",
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
      providesTags: (_, __, { id }) => [{ type: TAGS.SETTLEMENT_LIMIT, id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCMAccountsQuery,
  useLazyGetProviderBalanceQuery,
  useLazyGetSettlementScheduleConfigQuery,
  useLazyGetSettlementBalanceQuery,
  useGetSettlementLimitQuery,
} = extendedApi;
